import { ModalConfirmation, useModal } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { ModalId } from '../../../../../../../../../../enums/modalId'
import { useInvoiceEditor } from '../../../../../../contexts/invoiceEditorContext'
import { InvoiceEditorView } from '../../../../../../enums/invoiceEditorView'
import { InvoicePreviewAppearanceProps } from '../../types/invoicePreviewAppearanceProps'
import { InvoicePreviewAppearanceFormContextProvider } from './contexts/invoicePreviewAppearanceFormContext'
import { InvoicePreviewAppearanceSettingsContent } from './elements/InvoicePreviewAppearanceSettingsContent'

interface InvoicePreviewAppearanceSettingsProps extends InvoicePreviewAppearanceProps {
  isPreviewLoading: boolean
}

export const InvoicePreviewAppearanceSettings = ({
  documentType,
  updateInvoicePreviewAppearance,
  isPreviewLoading,
}: InvoicePreviewAppearanceSettingsProps): ReactElement => {
  const { t } = useTranslation()
  const { close: closeLeavePreviewModal } = useModal(ModalId.InvoiceEditorLeavePreviewModal)
  const { isAppearanceSettingsDirtyRef, setView } = useInvoiceEditor()

  const handleAppearanceSettingsDirtyChange = useCallback(
    (isDirty: boolean) => {
      isAppearanceSettingsDirtyRef.current = isDirty
    },
    [isAppearanceSettingsDirtyRef],
  )

  const handleLeavePreviewModalOkClick = useCallback(() => {
    setView(InvoiceEditorView.Editor)
    isAppearanceSettingsDirtyRef.current = false
    closeLeavePreviewModal()
  }, [closeLeavePreviewModal, isAppearanceSettingsDirtyRef, setView])

  return (
    <InvoicePreviewAppearanceFormContextProvider
      isInvoicePreviewLoading={isPreviewLoading}
      onDirtyChange={handleAppearanceSettingsDirtyChange}
    >
      <InvoicePreviewAppearanceSettingsContent
        documentType={documentType}
        updateInvoicePreviewAppearance={updateInvoicePreviewAppearance}
      />
      <ModalConfirmation
        cancelLabel={t('external_invoices.appearance_settings.leave_preview_modal.cancel')}
        danger
        id={ModalId.InvoiceEditorLeavePreviewModal}
        message={t('external_invoices.appearance_settings.leave_preview_modal.content')}
        okLabel={t('external_invoices.appearance_settings.leave_preview_modal.confirm')}
        onOk={handleLeavePreviewModalOkClick}
        title={t('external_invoices.appearance_settings.leave_preview_modal.title')}
      />
    </InvoicePreviewAppearanceFormContextProvider>
  )
}
